import { FieldArray, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { FieldForm } from "components/RFQ/Input";
import { Flex, Grid, GridItem, Spinner } from "@chakra-ui/react";
import { cn, showToast } from "utils/helpers";
import { Icon } from "assets/Icon";
import { Button } from "newui/components/Button";
import { TextArea } from "components/FormInputs/TextArea";
import { apiV1 } from "api-v2";
import { Checkbox } from "components/RFQ/Checkbox";
import { GUEST_INVITE_TYPE } from "constants/guest";
import { useCurrentUser } from "hooks/auth";

export const GuestSelectorV2 = ({
	additionalMessage,
	handleAdditionalMessageChange,
	onClose,
	afterSubmit,
	rfxUuid,
}) => {
	const [currentFieldIndex, setCurrentFieldIndex] = useState(null);
	const [isInvitedSuccess, setIsInvitedSuccess] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [shouldFocus, setShouldFocus] = useState(null);
	const { currentUser } = useCurrentUser();

	// eslint-disable-next-line no-unused-vars
	const [suggestionStates, setSuggestionStates] = useState({});
	const nameFieldRefs = useRef({});

	const initialValues = {
		fieldData: [{ name: "", email: "", supplier_id: null }],
		additional_message: "",
	};

	// Handle focus after render
	useEffect(() => {
		if (shouldFocus !== null) {
			const input = nameFieldRefs.current[shouldFocus];
			if (input) {
				input.focus();
			}
			setShouldFocus(null);
		}
	}, [shouldFocus]);

	const validationSchema = yup.object().shape({
		fieldData: yup.array().of(
			yup.object().shape({
				first_name: yup.string().required("First Name is required"),
				last_name: yup.string().required("Last Name is required"),
				email: yup
					.string()
					.email("Invalid email")
					.required("Email is required")
					.max(254, "Email must be 254 characters or less"),
			})
		),
		additional_message: yup.string(),
	});

	const handleSubmit = async (values, { setSubmitting }) => {
		setSubmitting(true);

		try {
			// Prepare the payload array - one object per guest
			const guestInvites = {
				invitee_company_id: currentUser?.company_id,
				invited_by_user_id: currentUser?.id,
				invite_type_id: GUEST_INVITE_TYPE.RFX_PRE_APPROVER,
				additional_message: additionalMessage,
				invitees: values.fieldData.map((field) => ({
					first_name: field.first_name,
					last_name: field.last_name,
					email: field.email,
				})),
			};

			// Make the API call
			const response = await apiV1.post(
				`guest/invite/${rfxUuid}`,
				guestInvites
			);

			if (!response?.data?.success) {
				throw new Error(response?.data?.message);
			}

			setIsInvitedSuccess(true);
			showToast("Guests invited successfully!", "Success", true);

			afterSubmit && afterSubmit(response.data.data?.rfxApprovers);
			onClose && onClose();
		} catch (error) {
			console.error("Error inviting guests:", error);
			showToast(error.message, "error");
			setIsInvitedSuccess(false);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			validateOnMount={true}
			onSubmit={handleSubmit}
		>
			{({
				values,
				isValid,
				dirty,
				isSubmitting,
				setFieldValue,
				setFieldTouched,
			}) => {
				const handleEmailChange = (e, index) => {
					const newValue = e.target.value;

					setFieldValue(`fieldData.${index}.email`, newValue);
					setCurrentFieldIndex(index);
				};

				const handleRemoveField = (index, remove) => {
					if (currentFieldIndex === index) {
						setCurrentFieldIndex(null);
					} else if (currentFieldIndex > index) {
						setCurrentFieldIndex((prev) => prev - 1);
					}
					remove(index);
				};

				const handleAddField = (push) => {
					const newIndex = values.fieldData.length;
					push({ name: "", email: "", supplier_id: null });
					setShouldFocus(newIndex);
				};

				return (
					<Form>
						<FieldArray name="fieldData">
							{({ push, remove }) => (
								<div>
									{values.fieldData.map((fd, index) => (
										<div key={index}>
											<div className="mb-5 rounded-md bg-utility-blue-light-100 p-2">
												{/* <h4 className="text-xl/2 font-bold">
													Approver {index + 1}
												</h4> */}
												<Grid
													className="group mb-5 text-sm"
													templateColumns={{
														base: "1fr",
														md: "200px 200px 1fr 20px",
													}}
													gap={4}
												>
													<GridItem>
														<FieldForm
															ref={(el) => {
																if (el) {
																	const input = el.querySelector("input");
																	if (input) {
																		nameFieldRefs.current[index] = input;
																	}
																}
															}}
															label="First Name"
															type="text"
															maxLength="150"
															placeholder="First Name"
															name={`fieldData.${index}.first_name`}
															classNameBoxInput="h-1"
															className="min-w-0 flex-1"
															required
														/>
													</GridItem>
													<GridItem>
														<FieldForm
															ref={(el) => {
																if (el) {
																	const input = el.querySelector("input");
																	if (input) {
																		nameFieldRefs.current[index] = input;
																	}
																}
															}}
															label="Last Name"
															type="text"
															maxLength="150"
															placeholder="Last Name"
															name={`fieldData.${index}.last_name`}
															classNameBoxInput="h-1"
															className="min-w-0 flex-1"
															required
														/>
													</GridItem>
													<GridItem>
														<FieldForm
															label="Email"
															type="text"
															maxLength="254"
															placeholder="person@email.com"
															name={`fieldData.${index}.email`}
															onChange={(e) => handleEmailChange(e, index)}
															classNameBoxInput="h-1"
															className="flex-2 min-w-0"
															required
														/>
													</GridItem>
													<GridItem>
														{values.fieldData.length !== 1 && (
															<div
																className={cn(
																	"mt-7 cursor-pointer rounded-full pb-2 pt-2 text-center hover:bg-red-200"
																)}
															>
																<Icon
																	icon="trash"
																	className=""
																	style={{
																		width: "20px",
																		height: "20px",
																	}}
																	onClick={() =>
																		handleRemoveField(index, remove)
																	}
																/>
															</div>
														)}
													</GridItem>
												</Grid>
											</div>
										</div>
									))}
									<Flex
										gap={4}
										align={"center"}
										className="pb-5"
									>
										<Button
											btntype="secondary"
											type={"button"}
											className="!w-max"
											onClick={() => handleAddField(push)}
											disabled={!isValid || isSubmitting}
										>
											<Flex
												gap={2}
												className="items-center"
											>
												<Icon icon="plus-blue" />
												Add another
											</Flex>
										</Button>
									</Flex>
								</div>
							)}
						</FieldArray>
						<div className="">
							<TextArea
								value={additionalMessage}
								label="Additional Message"
								name="additional_message"
								placeholder="Additional Message"
								className="flex flex-col"
								errorClassName="!mt-1"
								onChange={handleAdditionalMessageChange}
							/>
						</div>
						<div
							className="mb-5 mt-2 flex cursor-pointer items-center justify-start"
							onClick={() => {
								setIsChecked(!isChecked);
							}}
						>
							<div className="w-30">
								<Checkbox
									checked={isChecked}
									onChange={() => {
										setIsChecked(!isChecked);
									}}
								/>
							</div>
							<div
								className="ml-3 flex-1 font-roboto font-medium text-gray-700"
								style={{ fontSize: "14px" }}
							>
								I've verified the above details and all email addresses are
								correct.
							</div>
						</div>
						<Button
							type="submit"
							style={{ width: "100%" }}
							disabled={!isChecked || !isValid || !dirty || isSubmitting}
						>
							{isSubmitting ? <Spinner></Spinner> : "Send Approval Request"}
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};
